import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GalleryImageDialog } from "./GalleryImageDialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface GalleryManagerProps {
  elementId: string;
  elementType: "mold" | "work_order" | "product_model" | "machine" | "batch" | "customer";
  title?: string;
}

export function GalleryManager({
  elementId,
  elementType,
  title = "Galería de Fotos",
}: GalleryManagerProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: images, isLoading } = useQuery({
    queryKey: ["gallery", elementId, elementType],
    queryFn: async () => {
      const { data, error } = await api
        .from("gallery")
        .select("*, profiles:uploaded_by(full_name)")
        .eq("element_id", elementId)
        .eq("element_type", elementType)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("gallery").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery", elementId, elementType] });
      toast({
        title: "Éxito",
        description: "Imagen eliminada correctamente",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDelete = (id: string) => {
    if (confirm("¿Está seguro de eliminar esta imagen?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>
              URLs de imágenes asociadas a este elemento
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setIsDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar Imagen
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground">Cargando...</p>
        ) : images?.length === 0 ? (
          <p className="text-muted-foreground">No hay imágenes registradas</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images?.map((image) => (
              <div
                key={image.id}
                className="border rounded-lg p-4 space-y-2 group relative"
              >
                <div className="aspect-video bg-muted rounded-md overflow-hidden">
                  <img
                    src={image.image_url}
                    alt={image.description || "Imagen"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.svg";
                    }}
                  />
                </div>
                {image.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {image.description}
                  </p>
                )}
                <div className="flex justify-between items-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(image.image_url, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(image.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <GalleryImageDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          elementId={elementId}
          elementType={elementType}
        />
      </CardContent>
    </Card>
  );
}
